<template>
  <aside class="project-one-right-rail" qaid="project-one-right-rail">
    <project-one-right-rail-header
      :base-properties="baseProperties"
      :group-id="groupId"
      :is-back-button-enabled="true"
      @on-backpress="returnToPreviousRailView"
    />

    <section class="project-one-right-rail__body" qaid="project-one-right-rail-body">
      <form-group
        type="radio"
        name="personalizationChoices"
        qaid="personalization-choices-radio"
        :multi-items="personalizeOptions"
        :value="currentSelection.choiceId"
        @change="handlePersonalizationChoiceUpdate"
      />
      <div v-if="personalizeFontOptions" class="mt-5">
        <form-group
          v-model="personalizationText"
          v-validate="'enCharsOnly'"
          type="text"
          name="personalizationText"
          :maxlength="20"
          :placeholder="bp('personalizePlaceholder')"
          qaid="personalization-text-input"
          :show-error-messages="true"
          :error="bp('personalizeAllowedCharacters')"
          @input="handlePersonalizationTextChange"
        />
        <form-group
          type="radio"
          name="personalizationFontChoices"
          qaid="personalization-font-choices-radio"
          :multi-items="personalizeFontOptions"
          :value="currentFontSelection.choiceId"
          @change="handlePersonalizationFontChoiceUpdate"
        />
        <project-one-personalize-font-preview
          v-if="personalizationText.length"
          :base-properties="baseProperties"
          :personalization-text="personalizationText"
          :font-choice-id="currentFontSelection.choiceId"
        />
      </div>
    </section>

    <project-one-right-rail-footer
      :base-properties="baseProperties"
      :button-action="confirmSelection"
      button-icon="check"
      :button-icon-left="true"
      :button-primary="true"
      :button-text="bp('done')"
    />
  </aside>
</template>

<script>
import {mapActions, mapGetters, mapState, mapMutations} from 'vuex';
import ProjectOneRightRailFooter from '@/projectOne/components/rightRail/components/ProjectOneRightRailFooter';
import ProjectOneRightRailHeader from '@/projectOne/components/rightRail/components/ProjectOneRightRailHeader';
import ProjectOnePersonalizeFontPreview from '@/projectOne/components/shared/ProjectOnePersonalizeFontPreview';
import FormGroup from '@/components/FormGroup';
import debounce from 'lodash/debounce';
import {Validator} from 'vee-validate';

export default {
  components: {ProjectOneRightRailFooter, ProjectOneRightRailHeader, FormGroup, ProjectOnePersonalizeFontPreview},

  props: {
    selectItemModule: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      personalizationText: '',
      fontOptionsGroupId: 36,
      personalizeOptionsGroupId: 35,
    };
  },

  computed: {
    contentData() {
      if (this.activeRailData?.content?.groupId && Array.isArray(this.moduleGroups)) {
        return this.moduleGroups.find((group) => group.id === this.activeRailData.content.groupId);
      }

      return null;
    },

    personalizeOptions() {
      const choices = [...(this.contentData?.choices || [])];

      let choiceMapping = choices.map((choice) => {
        return {
          value: choice.id,
          displayName: choice.description,
        };
      });
      choiceMapping.sort((a, b) => {
        return b.value - a.value;
      });
      return choiceMapping;
    },

    personalizeFontOptions() {
      const secondaryGroup = this.moduleGroups.find((group) => group.id === this.fontOptionsGroupId);
      let secondaryChoiceMapping = null;
      if (secondaryGroup && this.groupId === this.personalizeOptionsGroupId) {
        const secondaryChoices = [...secondaryGroup.choices];
        secondaryChoiceMapping = secondaryChoices.map((choice) => {
          return {
            value: choice.id,
            displayName: choice.description,
          };
        });
      }
      return secondaryChoiceMapping;
    },

    currentSelection() {
      const selection = this.selections.find((selection) => selection.groupId === this.contentData?.id);
      return selection || {};
    },

    currentFontSelection() {
      const selection = this.selections.find((selection) => selection.groupId === this.fontOptionsGroupId);
      return selection || {};
    },

    groupId() {
      return this.contentData?.id;
    },

    topTierPackageId() {
      return this.contentData?.topTierPackageId;
    },

    ...mapGetters('projectOne', ['activeRailData']),
    ...mapState('projectOne', ['moduleGroups', 'railDataStack', 'selections']),
  },

  created() {
    if (this.currentSelection?.personalizationText) {
      this.personalizationText = this.currentSelection.personalizationText;
    }
    Validator.extend('enCharsOnly', {
      getMessage: () => this.bp('personalizeAllowedCharacters'),
      validate: (value) => {
        // if field has non-en characters we use regex to detect them and then fail the validation
        if (value?.match(/[A-za-z0-9 !@#$%^&*()_\-+=[\]{}|;:'",<.>/?~`]/g)?.length === value?.length) {
          return true;
        } else {
          return false;
        }
      },
    });
  },

  methods: {
    confirmSelection() {
      this.$notify({
        type: 'success',
        message: this.bp('added'),
      });

      this.returnToInitialRailView();
    },

    handlePersonalizationChoiceUpdate(evt) {
      this.updateSelection({
        buildId: this.$route.params.buildId,
        module: 'personalize',
        selections: [{groupId: this.groupId, choiceId: evt.target.value, topTierPackageId: this.topTierPackageId}],
      });
    },

    handlePersonalizationFontChoiceUpdate(evt) {
      this.updateSelection({
        buildId: this.$route.params.buildId,
        module: 'personalize',
        selections: [
          {groupId: this.fontOptionsGroupId, choiceId: evt.target.value, topTierPackageId: this.topTierPackageId},
        ],
      });
    },

    handlePersonalizationTextChange: debounce(function(value) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.updateSelection({
            buildId: this.$route.params.buildId,
            module: 'personalize',
            selections: [
              {
                groupId: this.groupId,
                choiceId: this.currentSelection.choiceId,
                topTierPackageId: this.topTierPackageId,
                personalizationText: value,
              },
            ],
          });
        }
      });
    }, 300),

    returnToInitialRailView() {
      while (this.railDataStack?.length > 1) {
        this.popRailDataStack();
      }
    },

    returnToPreviousRailView() {
      if (this.railDataStack?.length > 1) {
        this.popRailDataStack();
      } else {
        this.$emit('on-backpress');
      }
    },

    ...mapActions('projectOne', ['updateSelection']),
    ...mapMutations('projectOne', ['popRailDataStack', 'pushToRailDataStack']),
  },
};
</script>
